/**
 * PURPOSE: Pestaña "Resultados" de Atletismo en la página del deporte de un evento (Winscore admin).
 * RESPONSIBILITIES: Alternar entre "Pruebas" (maestro–detalle: lista de pruebas a la izquierda,
 *   detalle y captura a la derecha), "Clasificación" (por universidad) y "Publicación en Hayllis
 *   2.0"; recordar la última prueba y los filtros en localStorage; y avisar antes de perder marcas sin
 *   guardar (cambio de prueba, de vista, recarga de la página o salida de la pestaña).
 *
 *   EL CLIENTE SOLO CAPTURA. Puestos, puntos, finales, mejores marcas y clasificación los calcula y
 *   devuelve el servidor (backend_winscore/docs/atletismo-contract.md). Aquí se muestran tal cual.
 *
 *   PERMISOS. `status` (GET .../status) llega desde CategoriaSection: la pestaña solo existe con
 *   `enabled: true`, y `canWrite`/`canOfficialize`/`canReopen` habilitan acciones. Es una pista de
 *   interfaz: el backend vuelve a validar en cada petición.
 * COLLABORATORS: sections/CategoriaSection.jsx, hooks/useAthleticsResults.js,
 *   hooks/useAthleticsResultsStatus.js, components/*.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/AtletismoResultadosArea.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { FiChevronDown, FiChevronUp, FiDownload, FiLock, FiRefreshCw } from "react-icons/fi";

import { athleticsResultsKeys, useAthleticsPruebas } from "../../hooks/useAthleticsResults.js";
import {
  ATHLETICS_RESULTS_STATUS_OFF,
  downloadAthleticsEventLynx,
  saveAthleticsFile,
} from "../../services/athleticsResultsService.js";
import { parseAthleticsError } from "../../utils/athleticsResultsErrors.js";
import { EMPTY_PRUEBA_FILTERS } from "../../utils/athleticsSearch.js";
import { DISCARD_CHANGES_OPTIONS, useConfirmDialog } from "./components/ConfirmDialog.jsx";
import PruebaDetailPanel from "./components/PruebaDetailPanel.jsx";
import PruebaListPanel from "./components/PruebaListPanel.jsx";
import PublicationSettingsPanel from "./components/PublicationSettingsPanel.jsx";
import StandingsView from "./components/StandingsView.jsx";
import { Badge, Card, EmptyState, SegmentedControl, Spinner } from "./components/ui.jsx";

const VIEW_OPTIONS = [
  { value: "pruebas", label: "Pruebas" },
  { value: "clasificacion", label: "Clasificación" },
  { value: "publicacion", label: "Publicación en Hayllis 2.0" },
];

const storageKey = (idevent, idsport, name) =>
  `winscore.athletics.${Number(idevent)}.${Number(idsport)}.${name}`;

const readStorage = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null || value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Sin almacenamiento (modo privado, bloqueado): la pantalla funciona igual, solo no recuerda.
  }
};

/** Solo las claves conocidas y como texto: un valor viejo o manipulado no rompe los filtros. */
const sanitizeFilters = (stored) => {
  const clean = { ...EMPTY_PRUEBA_FILTERS };
  if (stored && typeof stored === "object") {
    Object.keys(EMPTY_PRUEBA_FILTERS).forEach((key) => {
      if (typeof stored[key] === "string" || typeof stored[key] === "number") clean[key] = String(stored[key]);
    });
  }
  return clean;
};

const sanitizeId = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

const AtletismoResultadosArea = ({ status = ATHLETICS_RESULTS_STATUS_OFF, onDirtyChange }) => {
  const { idevent, idsport } = useParams();
  const queryClient = useQueryClient();
  const listQuery = useAthleticsPruebas({ idevent, idsport, enabled: status.enabled === true });
  const { confirm, dialog } = useConfirmDialog();

  const [view, setView] = useState("pruebas");
  const [filters, setFilters] = useState(() =>
    sanitizeFilters(readStorage(storageKey(idevent, idsport, "filters"), null))
  );
  const [selectedId, setSelectedId] = useState(() =>
    sanitizeId(readStorage(storageKey(idevent, idsport, "lastPrueba"), null))
  );
  const [subId, setSubId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);

  const handleDirtyChange = useCallback((value) => setDirty(Boolean(value)), []);

  useEffect(() => {
    writeStorage(storageKey(idevent, idsport, "filters"), filters);
  }, [filters, idevent, idsport]);

  useEffect(() => {
    writeStorage(storageKey(idevent, idsport, "lastPrueba"), selectedId);
  }, [selectedId, idevent, idsport]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(
    () => () => {
      if (onDirtyChange) onDirtyChange(false);
    },
    [onDirtyChange]
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const pruebas = useMemo(() => listQuery.data?.pruebas || [], [listQuery.data]);
  const selectedSummary = useMemo(
    () => pruebas.find((prueba) => Number(prueba.id) === Number(selectedId)) || null,
    [pruebas, selectedId]
  );

  // Una prueba recordada que ya no está en el catálogo no se pide: se olvida.
  useEffect(() => {
    if (!listQuery.data || selectedId === null) return;
    if (!selectedSummary) {
      setSelectedId(null);
      setSubId(null);
    }
  }, [listQuery.data, selectedId, selectedSummary]);

  const allowLeave = useCallback(async () => {
    if (!dirty) return true;
    return confirm(DISCARD_CHANGES_OPTIONS);
  }, [confirm, dirty]);

  const selectPrueba = async (id) => {
    if (Number(id) === Number(selectedId) && subId === null) {
      setListCollapsed(true);
      return;
    }
    if (!(await allowLeave())) return;
    setSelectedId(sanitizeId(id));
    setSubId(null);
    setListCollapsed(true);
  };

  /** Sub-prueba elegida desde la lista (subtareas de una combinada): abre la combinada en esa prueba. */
  const selectSubPrueba = async (parentId, childId) => {
    if (Number(parentId) === Number(selectedId) && Number(childId) === Number(subId)) {
      setListCollapsed(true);
      return;
    }
    if (!(await allowLeave())) return;
    setSelectedId(sanitizeId(parentId));
    setSubId(sanitizeId(childId));
    setListCollapsed(true);
  };

  const openSubPrueba = async (id) => {
    if (!(await allowLeave())) return;
    setSubId(sanitizeId(id));
  };

  const backToParent = async () => {
    if (!(await allowLeave())) return;
    setSubId(null);
  };

  const changeView = async (next) => {
    if (next === view) return;
    if (view === "pruebas" && !(await allowLeave())) return;
    setView(next);
  };

  const refreshAll = () => {
    queryClient.invalidateQueries(athleticsResultsKeys.scope(idevent, idsport)).catch(() => undefined);
  };

  // FinishLynx completo: lynx.evt + lynx.sch de TODAS las pruebas de pista (todas las fases), leídos de la base en el momento.
  const [lynxBusy, setLynxBusy] = useState(false);
  const downloadFullLynx = async () => {
    setLynxBusy(true);
    try {
      const evt = await downloadAthleticsEventLynx({ idevent, idsport, kind: "evt" });
      const sch = await downloadAthleticsEventLynx({ idevent, idsport, kind: "sch" });
      saveAthleticsFile(evt);
      saveAthleticsFile(sch);
      toast.success(`Descargados: ${evt.filename} y ${sch.filename}`);
    } catch (error) {
      toast.error(parseAthleticsError(error).message);
    } finally {
      setLynxBusy(false);
    }
  };

  const permissions = status;
  const detailProps = {
    idevent,
    idsport,
    permissions,
    onDirtyChange: handleDirtyChange,
  };

  return (
    <div className="w-full space-y-4 text-left font-quickSand">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl options={VIEW_OPTIONS} value={view} onChange={changeView} ariaLabel="Vista de atletismo" />
        <div className="flex flex-wrap items-center gap-2">
          {!status.canWrite ? (
            <Badge tone="slate" icon={FiLock} title="Tu rol solo permite consultar">
              Solo lectura
            </Badge>
          ) : null}
          {view === "pruebas" ? (
            <button
              type="button"
              onClick={downloadFullLynx}
              disabled={lynxBusy}
              title="Descarga lynx.evt y lynx.sch con TODAS las pruebas de pista (todas las fases) para el Photo Finish"
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-bold text-emerald-800 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {lynxBusy ? <Spinner /> : <FiDownload aria-hidden="true" />} FinishLynx completo
            </button>
          ) : null}
          <button
            type="button"
            onClick={refreshAll}
            disabled={dirty || listQuery.isFetching}
            title={dirty ? "Guarda o descarta los cambios antes de actualizar." : "Volver a pedir los datos al servidor"}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {listQuery.isFetching ? <Spinner /> : <FiRefreshCw aria-hidden="true" />} Actualizar
          </button>
        </div>
      </div>

      {view === "pruebas" ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start">
          <Card className="overflow-hidden lg:sticky lg:top-4 lg:flex lg:max-h-[calc(100vh-2rem)] lg:flex-col">
            <button
              type="button"
              onClick={() => setListCollapsed((current) => !current)}
              className="flex w-full items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 text-left lg:hidden"
              aria-expanded={!listCollapsed}
            >
              <span className="min-w-0 truncate text-sm font-bold text-slate-800">
                {selectedSummary ? selectedSummary.label : "Pruebas"}
              </span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-indigo-600">
                {listCollapsed ? "Mostrar lista" : "Ocultar lista"}
                {listCollapsed ? <FiChevronDown aria-hidden="true" /> : <FiChevronUp aria-hidden="true" />}
              </span>
            </button>
            <div
              className={`${listCollapsed ? "hidden" : "flex"} max-h-[65vh] flex-col lg:flex lg:max-h-none lg:min-h-0 lg:flex-1`}
            >
              <PruebaListPanel
                listQuery={listQuery}
                filters={filters}
                onFiltersChange={setFilters}
                selectedId={selectedId}
                onSelect={selectPrueba}
                selectedSubId={subId}
                onSelectSub={selectSubPrueba}
                idevent={idevent}
                idsport={idsport}
              />
            </div>
          </Card>

          <div className="min-w-0">
            {selectedSummary ? (
              subId ? (
                <PruebaDetailPanel
                  key={`sub-${subId}`}
                  {...detailProps}
                  pruebaId={subId}
                  parent={{ id: selectedSummary.id, label: selectedSummary.label || selectedSummary.name }}
                  onBackToParent={backToParent}
                  onOpenSubPrueba={openSubPrueba}
                />
              ) : (
                <PruebaDetailPanel
                  key={`prueba-${selectedSummary.id}`}
                  {...detailProps}
                  pruebaId={selectedSummary.id}
                  onOpenSubPrueba={openSubPrueba}
                />
              )
            ) : (
              <Card>
                <EmptyState
                  title={listQuery.isLoading ? "Cargando pruebas..." : "Selecciona una prueba"}
                  description="Elige una prueba de la lista para ver sus series y capturar resultados."
                />
              </Card>
            )}
          </div>
        </div>
      ) : null}

      {view === "clasificacion" ? <StandingsView idevent={idevent} idsport={idsport} /> : null}

      {view === "publicacion" ? (
        <PublicationSettingsPanel
          idevent={idevent}
          idsport={idsport}
          canWrite={status.canWrite === true}
          publicEnabled={status.publicEnabled === true}
        />
      ) : null}

      {dialog}
    </div>
  );
};

export default AtletismoResultadosArea;
